import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
} from "@mui/material";
import { useMemo, useState } from "react";
import CustomTextField from "../../../../../../components/ui/Textfield";

type AddDocumentModalProps = {
  open: boolean;
  onClose: () => void;
  loading?: boolean;
  categories: string[];
  onSave: (payload: {
    name: string;
    description: string;
    category: string;
    file: File;
  }) => Promise<void>;
};

export default function AddDocumentModal({
  open,
  onClose,
  loading = false,
  categories,
  onSave,
}: AddDocumentModalProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const canSave = useMemo(() => {
    return (
      name.trim().length > 0 &&
      description.trim().length > 0 &&
      category.trim().length > 0 &&
      !!file
    );
  }, [name, description, category, file]);

  const handlePickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
  };

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!file) return;
    await onSave({
      name: name.trim(),
      description: description.trim(),
      category,
      file,
    });

    setName("");
    setDescription("");
    setCategory("");
    setFile(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>Add Document</DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2.5}>
          <CustomTextField
            placeholder="Document Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
          />

          <CustomTextField
            placeholder="Document Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
          />

          <CustomTextField
            select
            placeholder="Document Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            fullWidth
          >
            {categories.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </CustomTextField>

          <Box>
            <Button
              component="label"
              fullWidth
              sx={{
                bgcolor: "#724e41",
                color: "white",
                borderRadius: "10px",
                py: 1.3,
                "&:hover": { bgcolor: "#5d3e33" },
              }}
              disabled={loading}
            >
              UPLOAD FILE
              <input
                type="file"
                hidden
                accept="application/pdf"
                onChange={handlePickFile}
              />
            </Button>

            {file && (
              <Box sx={{ mt: 1, fontSize: 12, color: "text.secondary" }}>
                Selected: {file.name}
              </Box>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          CANCEL
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSave || loading}
          sx={{
            bgcolor: "#724e41",
            color: "white",
            "&:hover": { bgcolor: "#5d3e33" },
          }}
        >
          SAVE
        </Button>
      </DialogActions>
    </Dialog>
  );
}
